import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';

const token = () => localStorage.getItem('crm_token');
const taskApi = (path, opts = {}) => axios({ url: `/api/wedding-tasks${path}`, headers: { Authorization: `Bearer ${token()}` }, ...opts });
const resApi  = (path, opts = {}) => axios({ url: `/api/wedding-reservations${path}`, headers: { Authorization: `Bearer ${token()}` }, ...opts });

const CATS = {
  preparation: { label: '📋 Hazırlık',     color: '#7c3aed', bg: '#ede9fe' },
  day_of:      { label: '🎊 Gün İçi',      color: '#d97706', bg: '#fef3c7' },
  closing:     { label: '🔒 Kapanış',       color: '#059669', bg: '#d1fae5' },
};

export default function WeddingEventChecklist() {
  const [reservations, setReservations] = useState([]);
  const [selectedRes, setSelectedRes] = useState('');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addForm, setAddForm] = useState({ title: '', category: 'preparation', due_datetime: '' });
  const [adding, setAdding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    resApi('/').then(r => setReservations(r.data));
  }, []);

  const fetchTasks = async (rid) => {
    setLoading(true);
    const { data } = await taskApi(`/reservation/${rid}`);
    setTasks(data);
    setLoading(false);
  };

  const onResChange = (rid) => {
    setSelectedRes(rid);
    if (rid) fetchTasks(rid);
    else setTasks([]);
  };

  const toggle = async (task) => {
    await taskApi(`/${task.id}`, { method: 'put', data: { is_completed: !task.is_completed } });
    fetchTasks(selectedRes);
  };

  const deleteTask = async (id) => {
    await taskApi(`/${id}`, { method: 'delete' });
    fetchTasks(selectedRes);
  };

  const addTask = async () => {
    if (!addForm.title || !selectedRes) return;
    setAdding(true);
    try {
      await taskApi('/', { method: 'post', data: { ...addForm, reservation_id: selectedRes } });
      setAddForm({ title: '', category: 'preparation', due_datetime: '' });
      setShowAdd(false);
      fetchTasks(selectedRes);
    } finally { setAdding(false); }
  };

  const getCatTasks = (cat) => tasks.filter(t => t.category === cat);
  const completed = tasks.filter(t => t.is_completed).length;
  const progress = tasks.length > 0 ? Math.round((completed / tasks.length) * 100) : 0;

  const currentRes = reservations.find(r => String(r.id) === String(selectedRes));

  return (
    <div className="ec-page">
      <div className="ec-header">
        <div>
          <h1 className="ec-title">✅ Etkinlik Checklist</h1>
          <p className="ec-subtitle">Rezervasyon bazlı görev takibi</p>
        </div>
      </div>

      {/* Rezervasyon Seçici */}
      <div className="ec-res-select-wrap">
        <div className="ec-field">
          <label>Rezervasyon Seçin</label>
          <select className="ec-res-select" value={selectedRes} onChange={e => onResChange(e.target.value)}>
            <option value="">-- Rezervasyon seçin --</option>
            {reservations.map(r => (
              <option key={r.id} value={r.id}>
                {r.event_date} – {r.customer_first_name} {r.customer_last_name} ({r.status})
              </option>
            ))}
          </select>
        </div>
      </div>

      {!selectedRes && (
        <div className="ec-empty-state">
          <div style={{ fontSize: 64 }}>📋</div>
          <p>Bir rezervasyon seçerek checklist'i görüntüleyin</p>
        </div>
      )}

      {selectedRes && (
        <>
          {/* Progress Bar */}
          <div className="ec-progress-card">
            <div className="ec-progress-header">
              <div>
                <div className="ec-res-info">
                  {currentRes && (
                    <>💒 <strong>{currentRes.customer_first_name} {currentRes.customer_last_name}</strong> — {currentRes.event_date && format(parseISO(currentRes.event_date), 'dd MMMM yyyy', { locale: tr })}</>
                  )}
                </div>
                <div className="ec-progress-label">{completed} / {tasks.length} görev tamamlandı</div>
              </div>
              <div className="ec-progress-pct">{progress}%</div>
            </div>
            <div className="ec-progress-bar-bg">
              <div className="ec-progress-bar-fill" style={{ width: `${progress}%` }} />
            </div>
          </div>

          {/* Add Task Button */}
          <div className="ec-add-section">
            {!showAdd ? (
              <button className="ec-btn-add" onClick={() => setShowAdd(true)}>+ Görev Ekle</button>
            ) : (
              <div className="ec-add-form">
                <input className="ec-input" placeholder="Görev adı *" value={addForm.title} onChange={e => setAddForm({...addForm, title: e.target.value})} />
                <select className="ec-select" value={addForm.category} onChange={e => setAddForm({...addForm, category: e.target.value})}>
                  {Object.entries(CATS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <input className="ec-input" type="datetime-local" value={addForm.due_datetime} onChange={e => setAddForm({...addForm, due_datetime: e.target.value})} />
                <button className="ec-btn-save" onClick={addTask} disabled={adding}>{adding ? '...' : 'Ekle'}</button>
                <button className="ec-btn-cancel" onClick={() => setShowAdd(false)}>İptal</button>
              </div>
            )}
          </div>

          {/* Task Categories */}
          {loading ? (
            <div className="ec-loading">Yükleniyor...</div>
          ) : (
            <div className="ec-cat-grid">
              {Object.entries(CATS).map(([catKey, catInfo]) => {
                const catTasks = getCatTasks(catKey);
                const catCompleted = catTasks.filter(t => t.is_completed).length;
                return (
                  <div key={catKey} className="ec-cat-card">
                    <div className="ec-cat-header" style={{ borderLeft: `4px solid ${catInfo.color}` }}>
                      <span className="ec-cat-label" style={{ color: catInfo.color }}>{catInfo.label}</span>
                      <span className="ec-cat-count" style={{ background: catInfo.bg, color: catInfo.color }}>
                        {catCompleted}/{catTasks.length}
                      </span>
                    </div>
                    {catTasks.length === 0 && (
                      <div className="ec-cat-empty">Görev yok</div>
                    )}
                    {catTasks.map(task => (
                      <div key={task.id} className={`ec-task ${task.is_completed ? 'completed' : ''}`}>
                        <button className="ec-checkbox" onClick={() => toggle(task)}>
                          {task.is_completed ? '✅' : '⬜'}
                        </button>
                        <div className="ec-task-body">
                          <div className="ec-task-title">{task.title}</div>
                          {task.due_datetime && (
                            <div className="ec-task-due">
                              🕐 {format(new Date(task.due_datetime), 'dd MMM HH:mm', { locale: tr })}
                            </div>
                          )}
                        </div>
                        <button className="ec-del-btn" onClick={() => deleteTask(task.id)}>✕</button>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <style>{`
        .ec-page { padding:24px; max-width:1200px; }
        .ec-header { margin-bottom:20px; }
        .ec-title { font-size:24px; font-weight:700; color:#FFFFFF; margin:0; }
        .ec-subtitle { font-size:14px; color:#9CA3AF; margin:4px 0 0; }
        .ec-res-select-wrap { margin-bottom:20px; }
        .ec-field { display:flex; flex-direction:column; gap:6px; max-width:480px; }
        .ec-field label { font-size:13px; font-weight:600; color:#E5E7EB; }
        .ec-res-select { padding:10px 14px; border:1px solid rgba(255,255,255,0.08); border-radius:10px; font-size:14px; background:rgba(26,26,36,0.8); color:#FFFFFF; outline:none; width:100%; }
        .ec-res-select:focus { border-color:#3B82F6; box-shadow:0 0 0 3px rgba(59,130,246,0.1); }
        .ec-res-select option { background:#111118; }
        .ec-empty-state { text-align:center; padding:80px; color:#6B7280; background:rgba(17,17,24,0.7); border:1px solid rgba(255,255,255,0.06); border-radius:16px; }
        .ec-progress-card { background:rgba(17,17,24,0.7); border:1px solid rgba(255,255,255,0.06); border-radius:16px; padding:20px 24px; margin-bottom:16px; backdrop-filter:blur(12px); }
        .ec-progress-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:12px; }
        .ec-res-info { font-size:15px; color:#E5E7EB; margin-bottom:4px; }
        .ec-progress-label { font-size:13px; color:#9CA3AF; }
        .ec-progress-pct { font-size:32px; font-weight:800; color:#60A5FA; }
        .ec-progress-bar-bg { height:10px; background:rgba(255,255,255,0.06); border-radius:999px; overflow:hidden; }
        .ec-progress-bar-fill { height:100%; background:linear-gradient(90deg,#3B82F6,#2563EB); border-radius:999px; transition:width 0.4s ease; box-shadow:0 0 8px rgba(59,130,246,0.4); }
        .ec-add-section { margin-bottom:16px; }
        .ec-btn-add { padding:10px 20px; background:rgba(59,130,246,0.08); color:#60A5FA; border:1.5px dashed rgba(59,130,246,0.3); border-radius:10px; font-weight:600; cursor:pointer; font-size:14px; }
        .ec-add-form { display:flex; gap:10px; flex-wrap:wrap; align-items:center; background:rgba(17,17,24,0.7); border:1px solid rgba(255,255,255,0.06); padding:16px; border-radius:12px; backdrop-filter:blur(12px); }
        .ec-input { padding:9px 12px; border:1px solid rgba(255,255,255,0.08); border-radius:8px; font-size:14px; outline:none; flex:1; min-width:160px; background:rgba(26,26,36,0.8); color:#FFFFFF; }
        .ec-input:focus { border-color:#3B82F6; box-shadow:0 0 0 3px rgba(59,130,246,0.1); }
        .ec-select { padding:9px 12px; border:1px solid rgba(255,255,255,0.08); border-radius:8px; font-size:14px; background:rgba(26,26,36,0.8); color:#FFFFFF; }
        .ec-select option { background:#111118; }
        .ec-btn-save { padding:9px 18px; background:#3B82F6; color:#fff; border:none; border-radius:8px; font-weight:600; cursor:pointer; box-shadow:0 4px 12px rgba(59,130,246,0.3); }
        .ec-btn-cancel { padding:9px 14px; background:rgba(255,255,255,0.06); color:#E5E7EB; border:1px solid rgba(255,255,255,0.1); border-radius:8px; font-weight:600; cursor:pointer; }
        .ec-loading { text-align:center; padding:40px; color:#9CA3AF; }
        .ec-cat-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(300px,1fr)); gap:16px; }
        .ec-cat-card { background:rgba(17,17,24,0.7); border:1px solid rgba(255,255,255,0.06); border-radius:14px; padding:20px; backdrop-filter:blur(12px); }
        .ec-cat-header { display:flex; justify-content:space-between; align-items:center; padding-left:12px; margin-bottom:14px; }
        .ec-cat-label { font-size:15px; font-weight:700; }
        .ec-cat-count { font-size:12px; font-weight:600; padding:3px 10px; border-radius:20px; }
        .ec-cat-empty { text-align:center; padding:20px; color:#4B5563; font-size:13px; }
        .ec-task { display:flex; align-items:flex-start; gap:10px; padding:10px; border-radius:8px; transition:background 0.15s; }
        .ec-task:hover { background:rgba(59,130,246,0.05); }
        .ec-task.completed .ec-task-title { text-decoration:line-through; color:#6B7280; }
        .ec-checkbox { background:none; border:none; font-size:18px; cursor:pointer; flex-shrink:0; padding:0; line-height:1; }
        .ec-task-body { flex:1; }
        .ec-task-title { font-size:14px; font-weight:500; color:#E5E7EB; }
        .ec-task-due { font-size:12px; color:#6B7280; margin-top:2px; }
        .ec-del-btn { background:none; border:none; color:#374151; cursor:pointer; font-size:14px; padding:2px 4px; border-radius:4px; }
        .ec-del-btn:hover { color:#FCA5A5; background:rgba(239,68,68,0.1); }
      `}</style>
    </div>
  );
}
